import { getBlogRootPath } from "@/lib/paths";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { InstantSearchNextProps } from "react-instantsearch-nextjs";
type Router = Exclude<NonNullable<InstantSearchNextProps["routing"]>, boolean>;

export const useCustomAlgoliaRouting = (indexName: string | undefined) => {
  const pathname = usePathname();

  const routing = useMemo<Router>(
    () => ({
      router: {
        cleanUrlOnDispose: false,
        createURL({ qsModule, location, routeState }) {
          if (!indexName) return location.href;

          const indexState = routeState[indexName] || {};
          const queryParams = {
            query: indexState.query ?? undefined,
            page:
              indexState.page && indexState.page !== 1
                ? indexState.page
                : undefined,
            refinementList: indexState.refinementList ?? undefined
          };

          const queryString = qsModule.stringify(queryParams, {
            addQueryPrefix: true,
            arrayFormat: "repeat"
          });

          return `${location.origin}${getBlogRootPath()}${pathname}${queryString}`;
        },
        parseURL({ qsModule, location }) {
          if (!indexName) return {};

          const parsed = qsModule.parse(location.search, {
            ignoreQueryPrefix: true
          });

          const indexState: Record<string, unknown> = {};

          if (typeof parsed.query === "string") {
            indexState.query = parsed.query;
          }
          if (parsed.page) {
            indexState.page = Number(parsed.page);
          }
          if (
            parsed.refinementList &&
            typeof parsed.refinementList === "object"
          ) {
            const normalized: Record<string, string[]> = {};
            Object.entries(parsed.refinementList).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                normalized[key] = value.filter(
                  (v): v is string => typeof v === "string"
                );
              } else if (typeof value === "string") {
                normalized[key] = [value];
              }
            });
            indexState.refinementList = normalized;
          }

          return { [indexName]: indexState };
        }
      }
    }),
    [pathname, indexName]
  );

  return routing;
};
